import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button, Container, Grid, IconButton, makeStyles, Typography } from '@material-ui/core';
import { RemoveCircle } from '@material-ui/icons';
import CloseIcon from '@material-ui/icons/Close';

import SelfHelpCategorySelection from './SelfHelpCategorySelection';
import Resource, {
  DCM,
  DOCS,
  EDUCATIONAL,
  IMAGE,
  resourceCategories,
  resourcePrivacy,
  resourcePrivacyList,
  resourceTypes,
  SELFHELP,
  SUPPLEMENTARY,
  THREE,
  VIDEO,
} from '../../models/Resource';
import EditFields from '../common/EditFields';
import LoadingButton from '../common/LoadingButton';
import TagsTextField from '../common/TagsTextField';

export const imageFormats = ['jpg', 'jpeg', 'gif', 'bmp', 'png', 'tiff'];
export const videoFormats = ['m4v', 'avi', 'mpg', 'mp4', 'webm'];
export const threeFormats = ['glb'];
export const dicomFormats = ['dcm'];
export const documentFormats = ['pdf'];

const defaultData = {
  category: EDUCATIONAL,
  description: '',
  privacy: resourcePrivacy.GLOBAL,
  title: '',
  type: IMAGE,
};

const customThumbnailsVisibleTypes = [VIDEO, DOCS, DCM];

const useStyles = makeStyles((theme) => {
  return {
    bold: {
      fontWeight: 'bold',
    },
    buttonChooseCustomThumbnail: {
      marginTop: '16px',
    },
    buttonRemove: {
      border: '1px solid #FF0000',
      color: '#FF0000',
      marginRight: '8px',
      marginTop: '8px',
    },
    buttonReset: {
      marginRight: '8px',
      marginTop: '8px',
    },
    containerCustomThumbnailActions: {
      borderBottom: '1px solid #333',
      paddingBottom: '16px',
    },
    gridRow: {
      margin: theme.spacing(4, 0),
    },
    mainContainer: {
      padding: '30px',
    },
    tagContainer: {
      background: '#E7E7E7',
      borderRadius: '10px',
      display: 'inline-block',
      margin: '5px',
      padding: '5px 10px',
      textAlign: 'center',
    },
    tagName: {
      color: theme.palette.primary.main,
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: '500',
      lineHeight: '14px',
    },
    thumbnailImage: {
      height: 'auto',
      marginBottom: '4px',
      width: '100%',
    },
    title: {
      color: '#172B4D',
      fontSize: '20px',
      fontWeight: '500',
      lineHeight: '23px',
    },
    typographyError: {
      color: 'red',
      fontSize: '12px',
      marginBottom: '0px',
    },
  };
});

export default ({ resource, isMediphanyWorkspace, onAddResource, onValidateFile, anatomies, modalities, tags, isSubmitting$, selfHelpCategories }) => {
  const defaultPrivacy = isMediphanyWorkspace ? resourcePrivacy.GLOBAL : resourcePrivacy.WORKSPACE;
  defaultData.privacy = defaultPrivacy;

  let managedPrivacyList = resourcePrivacyList;
  if (!isMediphanyWorkspace) {
    managedPrivacyList = resourcePrivacyList.filter((item) => item.value !== resourcePrivacy.GLOBAL);
  }

  const typeFields = [
    {
      items: resourceTypes,
      label: 'Resource Type',
      name: 'type',
      onChange: (value) => {
        setSelectedType(value.type);
      },
      required: true,
      type: 'select',
    },
  ];

  const educationalFields = [
    { label: 'Resource Title', name: 'title', placeholder: 'Resource Title', required: true, type: 'text' },
    { label: 'Description', name: 'description', placeholder: 'Resource Description', required: false, type: 'text' },
    {
      items: resourceCategories,
      label: 'Resource Category',
      name: 'category',
      onChange: (value) => {
        const fieldsToSet = value.category === SUPPLEMENTARY ? supplementaryFields : educationalFields;
        setFields(fieldsToSet);
        setShowTags(value.category === EDUCATIONAL || value.category === SELFHELP);
        setSelectedCategory(value.category);
      },
      required: true,
      type: 'select',
    },
  ];

  const supplementaryFields = [
    { label: 'Resource Title', name: 'title', placeholder: 'Resource Title', required: true, type: 'text' },
    { label: 'Description', name: 'description', placeholder: 'Resource Description', required: false, type: 'text' },
    {
      items: resourceCategories,
      label: 'Resource Category',
      name: 'category',
      onChange: (value) => {
        const fieldsToSet = value.category === SUPPLEMENTARY ? supplementaryFields : educationalFields;
        setFields(fieldsToSet);
        setShowTags(value.category === EDUCATIONAL || value.category === SELFHELP);
        setSelectedCategory(value.category);
      },
      required: true,
      type: 'select',
    },
    { items: anatomies, label: 'Anatomy', name: 'anatomy', required: true, type: 'select' },
    { items: modalities, label: 'Modality', name: 'modality', required: true, type: 'select' },
  ];

  const privacyFields = [
    {
      items: managedPrivacyList,
      label: 'Privacy',
      name: 'privacy',
      onChange: (value) => {
        setSelectedPrivacy(value.privacy);
      },
      required: true,
      type: 'select',
    },
  ];

  const classes = useStyles();
  const dataFields = useRef();
  const typeField = useRef();
  const privacyField = useRef();
  const uploadRef = useRef();
  const customThumbnailUploadRef = useRef();
  const selfHelpCatergoryRef = useRef();
  const [fields, setFields] = useState(resource && resource.category === SUPPLEMENTARY ? supplementaryFields : educationalFields);

  const [files, setFiles] = useState(null);
  const [formData, setFormData] = useState(resource || defaultData);
  const [selectedType, setSelectedType] = useState(resource ? resource?.resource?.type : defaultData.type);
  const [selectedPrivacy, setSelectedPrivacy] = useState(resource ? resource?.privacy : defaultPrivacy);
  const [selectedCategory, setSelectedCategory] = useState(formData.category);
  const [fileValidation, setFileValidation] = useState(resource);
  const [showTags, setShowTags] = useState(resource?.category === EDUCATIONAL || resource?.category === SELFHELP || !resource);
  const [addedTags, setAddedTags] = useState(resource ? [...resource?.tags] : []);
  const [resourceTypeError, setResourceTypeError] = useState('');
  const [selfHelpCategorySelectionMode, setSelfHelpCategorySelectionMode] = useState('list');
  const [selfHelpCategory, setSelfHelpCategory] = useState(resource?.selfHelpCategory?.name || '');
  const [thumbnail, setThumbnail] = useState([]);
  const [thumbnailUrl, setThumbnailUrl] = useState(resource?.resource?.customThumbnailPath || '');

  const defaultCustomThumbnail = useMemo(() => {
    return resource?.resource?.customThumbnailPath;
  }, [resource]);

  const removeTag = useCallback(
    (index) => {
      addedTags.splice(index, 1);
      setAddedTags([...addedTags]);
    },
    [addedTags]
  );
  const onSubmit = useCallback(async () => {
    const validData = await dataFields.current.getValidData();

    const selfHelpCategoryData = selfHelpCatergoryRef?.current?.getValidData ? await selfHelpCatergoryRef.current.getValidData() : {};
    if (selectedCategory === 'self-help' && !selfHelpCategoryData) return;

    if (validData && (!fileValidation || !fileValidation.error || resource)) {
      const dataToCreate = {
        data: {
          ...validData,
          privacy: selectedPrivacy,
          type: selectedType,
        },
        files: resource ? null : files,
        tags: addedTags,
      };

      if (defaultCustomThumbnail) {
        if (!thumbnailUrl) dataToCreate.customThumbnailPath = '';
      }

      if (selectedCategory === SELFHELP) {
        dataToCreate.selfHelpCategoryName = selfHelpCategoryData.selfHelpCategoryName;

        if (customThumbnailsVisibleTypes.includes(selectedType)) {
          dataToCreate.thumbnail = thumbnail?.[0] || undefined;
        }
      }

      onAddResource(dataToCreate);
    }
  }, [selectedCategory, selectedPrivacy, fileValidation, resource, selectedType, files, addedTags, thumbnail, defaultCustomThumbnail, onAddResource, thumbnailUrl]);

  const addTag = (value) => {
    const trimmed = value.trim();
    if (addedTags.indexOf(trimmed) === -1) {
      addedTags.push(trimmed);
      setAddedTags([...addedTags]);
    }
  };

  useEffect(() => {
    const validationFunction = async () => {
      if (files) {
        const validation = await onValidateFile(files, selectedType, selectedCategory);
        setFileValidation(validation);
      } else {
        setFileValidation(null);
      }
    };
    validationFunction().then();
  }, [files, selectedType, selectedCategory]);

  useEffect(() => {
    if (selectedCategory === 'self-help') {
      if ([DOCS, VIDEO, DCM].includes(selectedType)) setResourceTypeError('');
      else setResourceTypeError('Self help materials only accept document, study or video type!');
    } else {
      setResourceTypeError('');
    }
  }, [selectedType, selectedCategory]);

  useEffect(() => {
    if (selectedType === DCM) {
      setFiles(null);
    }
  }, [selectedType]);

  useEffect(() => {
    if (resource) {
      setFormData(resource);
    }
  }, [resource]);

  const handleFileChange = (evt) => {
    const filteredFiles =
      selectedType === DCM
        ? [].filter.call(evt.target.files, (file) => {
            const parts = file.name.split('.');
            return parts.length === 1 || parts[parts.length - 1].toLowerCase() === DCM;
          })
        : evt.target.files;

    setFiles([...filteredFiles]);
    evt.target.value = null;
  };

  const handleThumbnailChange = (evt) => {
    setThumbnail([...evt.target.files]);

    const filereader = new FileReader();
    filereader.onload = (e) => {
      setThumbnailUrl(e.target.result);
    };

    if (evt.target.files?.length) filereader.readAsDataURL(evt.target.files[0]);

    evt.target.value = null;
  };

  const handleSelfHelpCategoryChange = (value) => {
    setSelfHelpCategory(value);
  };

  const handleSelfHelpCategorySelectionModeChnage = (value) => {
    setSelfHelpCategorySelectionMode(value);
  };

  const handleRemoveSelectedThumbnail = () => {
    setThumbnailUrl(defaultCustomThumbnail || '');
    setThumbnail([]);
  };

  const handleRemoveCustomThumbnailPath = () => {
    setThumbnailUrl('');
    setThumbnail([]);
  };

  const handleResetCustomThumbnailPath = () => {
    setThumbnailUrl(defaultCustomThumbnail || '');
    setThumbnail([]);
  };

  const renderTagsField = () => (
    <Grid container className={`${classes.gridRow}`}>
      <Grid item xs={12}>
        <Typography align="left" className="field-label">
          Resource Tags
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <TagsTextField onAdd={addTag} options={tags} />
      </Grid>
    </Grid>
  );

  const renderUploadSection = () => {
    let note = null;

    if (selectedType !== DCM && (resource || (files && files[0]))) {
      const fileName = resource ? resource.title : [].map.call(files, (el) => el.name).join(', ');
      note = <Typography className="field-label"> {fileName} </Typography>;
    } else if (selectedType === DCM && fileValidation && !fileValidation.error) {
      note = <Typography className="field-label"> {fileValidation.message} </Typography>;
    }

    if (files && fileValidation && fileValidation.error) {
      note = (
        <Typography className="field-label" style={{ color: '#B12A2A' }}>
          {fileValidation.message}
        </Typography>
      );
    }

    let accept = null;
    if (selectedType === IMAGE) {
      accept = imageFormats.map((e) => `.${e}`).join(',');
    } else if (selectedType === THREE) {
      accept = threeFormats.map((e) => `.${e}`).join(',');
    } else if (selectedType === VIDEO) {
      accept = videoFormats.map((e) => `.${e}`).join(',');
    } else if (selectedType === DOCS) {
      accept = documentFormats.map((e) => `.${e}`).join(',');
    }

    return (
      <>
        {!resource && (
          <>
            <input
              ref={uploadRef}
              onChange={handleFileChange}
              multiple={selectedType === DCM ? '' : null}
              directory={selectedType === DCM ? '' : null}
              webkitdirectory={selectedType === DCM ? '' : null}
              moxdirectory={selectedType === DCM ? '' : null}
              accept={accept}
              hidden
              id="raised-button-study"
              type="file"
            />
            <label htmlFor="raised-button-study">
              <Button color="primary" variant="outlined" component="span">
                Choose File(s)
              </Button>
            </label>
          </>
        )}
        {note}
      </>
    );
  };

  const renderCustomThumbnail = () => {
    if (selectedCategory !== SELFHELP) return null;
    if (!customThumbnailsVisibleTypes.includes(selectedType)) return null;

    const accept = imageFormats.map((e) => `.${e}`).join(',');
    let fileName = '';
    if (thumbnail?.length) {
      fileName = resource ? resource.title : [].map.call(thumbnail, (el) => el.name).join(', ');
    }

    const isDefaultCustomThumbnailVisible = !!defaultCustomThumbnail;
    const shouldShowResetButton = !!defaultCustomThumbnail && defaultCustomThumbnail !== thumbnailUrl;

    return (
      <>
        <Typography align="left" className="field-label">
          Custom Thumbnail
        </Typography>
        {thumbnailUrl && <img src={thumbnailUrl} alt="Resouce Thumbnail" className={classes.thumbnailImage} />}
        {isDefaultCustomThumbnailVisible && (
          <div className={classes.containerCustomThumbnailActions}>
            <Grid container alignItems="center">
              {shouldShowResetButton && (
                <Button onClick={handleResetCustomThumbnailPath} className={classes.buttonReset} color="primary" variant="outlined">
                  Reset Thumbnail
                </Button>
              )}

              {thumbnailUrl && (
                <Button onClick={handleRemoveCustomThumbnailPath} className={classes.buttonRemove} color="error" variant="outlined">
                  Remove Thumbnail
                </Button>
              )}
            </Grid>
          </div>
        )}

        <input ref={customThumbnailUploadRef} accept={accept} onChange={handleThumbnailChange} hidden id="custom-thumbnail-file-selection" type="file" />
        <label htmlFor="custom-thumbnail-file-selection">
          <Button color="primary" variant="outlined" component="span" className={classes.buttonChooseCustomThumbnail}>
            Choose File
          </Button>
        </label>

        {fileName && (
          <Grid container alignItems="center">
            <IconButton onClick={handleRemoveSelectedThumbnail}>
              <CloseIcon />
            </IconButton>
            <Typography className="field-label"> {fileName} </Typography>
          </Grid>
        )}
      </>
    );
  };

  const renderSelfHelpCategorySelection = () => {
    if (selectedCategory !== 'self-help') return undefined;
    return (
      <SelfHelpCategorySelection
        dataRef={selfHelpCatergoryRef}
        categoryList={selfHelpCategories}
        currentCategory={selfHelpCategory}
        onCategoryChange={handleSelfHelpCategoryChange}
        categorySelectionMode={selfHelpCategorySelectionMode}
        onCategorySelectionModeChange={handleSelfHelpCategorySelectionModeChnage}
      />
    );
  };

  return (
    <Container maxWidth="xs" className={classes.mainContainer}>
      <Typography variant="h5" align="left" paragraph className="page-header">
        {resource ? 'Edit Resource' : 'Add Resource'}
      </Typography>

      <Typography align="left" className="body-copy mb-1">
        Some content about adding a resource and what you can add and so on and so forth
      </Typography>

      <EditFields
        ref={typeField}
        fields={typeFields}
        validationErrors={resourceTypeError ? { type: [resourceTypeError] } : null}
        data={formData}
        labelPosition="top"
        labelClass={classes.bold}
        useAutoFocus={false}
      />
      {renderUploadSection()}
      <EditFields fields={fields} data={formData} labelPosition="top" labelClass={classes.bold} validate={Resource.validateCreate} useAutoFocus={false} ref={dataFields} />
      {renderSelfHelpCategorySelection()}

      {renderCustomThumbnail()}

      {showTags && renderTagsField()}
      {showTags && (
        <div className="mt-1 mb-1">
          {addedTags.map((tag, index) => (
            <div className={classes.tagContainer}>
              <IconButton onClick={() => removeTag(index)} style={{ padding: 0 }}>
                <RemoveCircle />
              </IconButton>
              <Typography className={classes.tagName}>{tag}</Typography>
            </div>
          ))}
        </div>
      )}

      <EditFields
        ref={privacyField}
        fields={privacyFields}
        validationErrors={resourceTypeError ? { type: [resourceTypeError] } : null}
        data={formData}
        labelPosition="top"
        labelClass={classes.bold}
        useAutoFocus={false}
      />

      <Grid container>
        <Grid item xs={12}>
          <LoadingButton variant="contained" color="primary" onClick={onSubmit} loading$={isSubmitting$}>
            {resource ? 'Edit Resource' : 'Add Resource'}
          </LoadingButton>
        </Grid>
      </Grid>
    </Container>
  );
};
